import {
  documentFrameSchema,
  parseProject,
  newNode,
  type DocumentFrame,
  type Project,
} from "./schema";
import { normalizedVectorPath } from "./vector-path";

/** Apply an imported image as this page's base, preserving project configuration and artwork. */
export function uploadBaseDocument(
  project: Project,
  pageId: string,
  imported: Project,
): Project {
  if (project.tenantId !== imported.tenantId)
    throw new Error("Base document must belong to this workspace");
  const source = imported.pages[0],
    image = source?.nodes[0];
  if (
    imported.pages.length !== 1 ||
    !source?.frame ||
    !source.width ||
    !source.height ||
    source.nodes.length !== 1 ||
    image.type !== "image"
  )
    throw new Error("Choose a PNG, JPEG, WebP or TIFF base image");
  const value = imported.fields.find(
    (field) => field.id === image.fieldId,
  )?.default;
  const assetId =
    typeof value === "string"
      ? value
      : value && typeof value === "object" && !Array.isArray(value)
        ? value.assetId
        : image.content;
  if (typeof assetId !== "string" || !/^asset_[a-zA-Z0-9_-]+$/.test(assetId))
    throw new Error("Base image has no managed artwork");
  const next = parseProject(project),
    page = next.pages.find((page) => page.id === pageId);
  if (!page) throw new Error("Page no longer exists");
  const base = {
    ...newNode("image"),
    name: "Base document",
    content: assetId,
    x: image.x,
    y: image.y,
    width: image.width,
    height: image.height,
    locked: true,
  };
  page.nodes = [
    base,
    ...page.nodes.filter((node) => node.id !== page.baseDocumentNodeId),
  ];
  page.baseDocumentNodeId = base.id;
  page.frame = structuredClone(source.frame);
  page.width = source.width;
  page.height = source.height;
  page.margin = 0;
  page.background = null;
  page.backgroundMode = "direct";
  if (
    next.assetLibrary &&
    !next.assetLibrary.some((asset) => asset.assetId === assetId)
  )
    next.assetLibrary.push({ assetId });
  return parseProject(next);
}
export interface FrameClip {
  frame: DocumentFrame;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}
export const frameTransform = (clip: FrameClip) =>
  `translate(${clip.x} ${clip.y}) rotate(${clip.rotation}) scale(${clip.width / clip.frame.width} ${clip.height / clip.frame.height})`;
export function shapeFrame(
  kind: "ellipse" | "rounded" | "rectangle",
): DocumentFrame {
  const path =
    kind === "ellipse"
      ? "M500 0C776.142 0 1000 223.858 1000 500C1000 776.142 776.142 1000 500 1000C223.858 1000 0 776.142 0 500C0 223.858 223.858 0 500 0Z"
      : kind === "rounded"
        ? "M100 0H900Q1000 0 1000 100V900Q1000 1000 900 1000H100Q0 1000 0 900V100Q0 0 100 0Z"
        : "M0 0H1000V1000H0Z";
  return documentFrameSchema.parse({
    name:
      kind === "ellipse"
        ? "Ellipse"
        : kind === "rounded"
          ? "Rounded rectangle"
          : "Rectangle",
    path,
    width: 1000,
    height: 1000,
  });
}
export function cropPageToShape(
  project: Project,
  pageId: string,
  nodeId: string,
): Project {
  const p = parseProject(project),
    pg = p.pages.find((p) => p.id === pageId),
    n = pg?.nodes.find((n) => n.id === nodeId);
  if (!pg || !n || !["rect", "ellipse", "path"].includes(n.type))
    throw new Error("Choose a rectangle, ellipse, or vector shape");
  if (n.rotation % 360 !== 0)
    throw new Error("Set the frame shape rotation to zero before cropping");
  pg.frame =
    n.type === "path"
      ? documentFrameSchema.parse({
          name: n.name,
          path: n.pathViewport ? normalizedVectorPath(n) : n.path,
          width: n.pathViewport ? n.width : n.pathWidth || n.width,
          height: n.pathViewport ? n.height : n.pathHeight || n.height,
          fillRule: n.fillRule,
        })
      : shapeFrame(
          n.type === "ellipse" ? "ellipse" : n.radius ? "rounded" : "rectangle",
        );
  if (n.type === "rect" && n.radius) {
    const r = Math.min(n.radius, n.width / 2, n.height / 2),
      w = n.width,
      h = n.height;
    pg.frame = {
      name: n.name,
      width: w,
      height: h,
      fillRule: "evenodd",
      path: `M${r} 0H${w - r}Q${w} 0 ${w} ${r}V${h - r}Q${w} ${h} ${w - r} ${h}H${r}Q0 ${h} 0 ${h - r}V${r}Q0 0 ${r} 0Z`,
    };
  }
  pg.width = n.width;
  pg.height = n.height;
  pg.background = null;
  pg.backgroundMode = "direct";
  pg.margin = 0;
  pg.nodes = pg.nodes.map((x) => ({ ...x, x: x.x - n.x, y: x.y - n.y }));
  pg.flowRegions = pg.flowRegions.map((r) => ({
    ...r,
    x: Math.max(0, r.x - n.x),
    y: Math.max(0, r.y - n.y),
  }));
  return parseProject(p);
}
